"use client";

import { useState, useEffect, useCallback, useMemo, useRef } from "react";

const PRICE_CALCULATE_THROTTLE_MS = 2000;
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { cardVariants } from "@/lib/animations";
import DatePickerCustom from "./DatePickerCustom";
import AgreementModal from "./AgreementModal";
import RequestForm from "./RequestForm";
import YachtRequestForm from "./YachtRequestForm";
import GuestRoomSelectorBlock from "@/components/ui/GuestRoomSelectorBlock";
import DailyTourGuests from "./DailyTourGuests";
import { useDailyTourCalendar } from "@/hooks/useDailyTourCalendar";
import ReservationModalGuests from "@/components/ui/ReservationModalGuests";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import { useTravelersPreferences } from "@/contexts/TravelersPreferencesContext";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import { format } from "date-fns";
import {
    loadRecaptchaScript,
    renderRecaptchaWidget,
    resetRecaptchaWidget,
    getRecaptchaToken,
    loadRecaptchaV3Script,
    getRecaptchaV3Token,
    loadTurnstileScript,
    renderTurnstileWidget,
    resetTurnstileWidget,
    getTurnstileToken,
    removeTurnstileWidget,
} from "@/lib/captcha";
import { useCaptchaConfig } from "@/hooks/useCaptchaConfig";

import { RESERVATION_CHECKOUT_COOKIE_NAME } from "@/lib/cookies";
import { formatCurrencyAmount, formatCurrencyBreakdown, type CurrencyForFormat } from "@/lib/formatCurrency";
import { useFormSubmitReadiness } from "@/hooks/useFormSubmitReadiness";
import {
    resolveSidebarFormHeading,
    resolveSidebarFormTitleKind,
    shouldShowRequestForm,
    shouldShowReservationForm,
} from "@/utils/tour-sidebar-form-mode";

const RESERVATION_CAPTCHA_CONTAINER_ID = "reservation-booking-captcha-container";

const RESERVATION_CHECKOUT_COOKIE = RESERVATION_CHECKOUT_COOKIE_NAME;
const RESERVATION_COOKIE_MAX_AGE_DAYS = 30;

interface Agreement {
    id: number;
    title: string;
    slug: string;
    is_required: boolean;
    content?: string;
}

type BookingFlowType = 'request_first' | 'instant_online' | 'bank_transfer_only';

interface ReservationBookingSidebarProps {
    productId: number;
    productType: string;
    productName?: string;
    /** Üründe aktif fiyat var mı? Fiyat varsa + uygun booking_flow ise rezervasyon formu, yoksa talep formu */
    hasPrice?: boolean;
    /** request_first = talep formu, instant_online / bank_transfer_only = online rezervasyon formu */
    bookingFlow?: BookingFlowType;
    bookingFlowConfigured?: boolean;
    language?: string;
    languageId?: number;
    agreements?: Agreement[];
    translations?: Record<string, string>;
    onReservationSubmit?: (reservationData: any) => void;
    className?: string;
    /** Minimum kişi sayısı (tur fiyatlandırması için, örn. min 3 kişi) */
    minPax?: number | null;
    /** Sayfa render sırasında çekilmiş talep formu detayı; verilirse RequestForm ek istek atmaz */
    initialRequestFormDetails?: unknown;
    /** Yacht için minimum kiralama süresi (gün); YachtRequestForm takvim kısıtı için */
    minRentalDuration?: number | null;
    /** Yat dönem başlangıç tarihi (YYYY-MM-DD); takvim sınırlaması için */
    periodStartDate?: string | null;
    /** Yat dönem bitiş tarihi (YYYY-MM-DD); takvim sınırlaması için */
    periodEndDate?: string | null;
    /** Yacht kiralama tipi: daily | weekly | monthly; talep formunda bilgilendirme için */
    rentalType?: string;
    /** Tur/ship_tour için paket listesi (Gold, Standard vb.); seçilen pakete göre fiyat hesaplanır */
    packages?: Array<{ packageName: string }>;
    /** Sayfa bölüm linkleri (örn. Tur çıkış tarihleri); sağ çubukta listelenir */
    sectionLinks?: Array<{ title: string; link: string; children?: Array<{ title: string; link: string }> }>;
    /** true ise "Book now" modalında kullanılıyor: her zaman rezervasyon formu (tarih, paket, oda, fiyat) gösterilir; talep formu gösterilmez */
    showAsReservationForm?: boolean;
    /** Tur/ship_tour için sayfa yükünde gelen private fiyatlar; verilirse /api/price/calculate yerine client-side hesaplama yapılır */
    tourPricing?: TourPricingPeriod[];
    /** Client-side fiyat için para birimi (tourPricing kullanıldığında) */
    currency?: PriceData['currency'];
    /** Ürün metas (child_pax_age_threshold_private, min_ages için) */
    productMetas?: { child_pax_age_threshold_private?: number; min_ages?: string | number | null } | null;
    /** Tur fiyat dönemi: günlük turda API'ye pricing_period_type=daily gönderilir */
    pricingPeriodType?: 'private' | 'daily' | 'group';
    /** Rezervasyon sözleşmeleri client'ta yüklenirken submit kapalı */
    agreementsLoading?: boolean;
    /** Talep formu CMS başlığı (forms/details title) */
    requestFormTitle?: string | null;
}

/** Backend tour_pricing period yapısı */
interface TourPricingRow {
    paxRange?: string;
    minPax?: number | null;
    maxPax?: number | null;
    perPersonDouble?: number | null;
    singleRoom?: number | null;
    extraBed?: number | null;
    childrenFee?: number | null;
    childRules?: Array<{ min_age: number; max_age: number; fee: number }>;
}
export interface TourPricingPeriod {
    periodId?: number | null;
    periodType?: string;
    packageName?: string;
    periodStart?: string;
    periodEnd?: string;
    rows?: TourPricingRow[];
}

/** Private fiyatı tour_pricing verisiyle client-side hesaplar (tek pax band, oda bazlı formül). ROADMAP 8.9 / 10.3 */
function calculatePrivatePriceFromTourPricing(
    tourPricing: TourPricingPeriod[],
    dateStr: string,
    selectedPackage: string,
    rooms: Array<{ adults: number; children: number; childAges: number[] }>,
    childPaxAgeThreshold: number,
    currency?: PriceData['currency']
): PriceData | null {
    const privatePeriods = (tourPricing ?? []).filter((p) => (p.periodType ?? 'private') === 'private');
    const pkg = (selectedPackage || 'STANDARD').trim().toUpperCase();
    const dateMatchedPeriods = privatePeriods.filter((p) => {
        const start = (p.periodStart ?? '').trim();
        const end = (p.periodEnd ?? '').trim();
        if (!start || !end) return true;
        return dateStr >= start && dateStr <= end;
    });
    const period =
        dateMatchedPeriods.find((p) => (p.packageName ?? 'STANDARD').trim().toUpperCase() === pkg) ??
        dateMatchedPeriods.find((p) => (p.packageName ?? 'STANDARD').trim().toUpperCase() === 'STANDARD') ??
        dateMatchedPeriods[0];
    if (!period?.rows?.length) return null;

    const totalPax = rooms.reduce((sum, room) => {
        const adults = Math.max(0, room.adults ?? 0);
        const countAsPax = (room.childAges ?? []).filter((age) => age >= childPaxAgeThreshold).length;
        return sum + adults + countAsPax;
    }, 0);

    const row = period.rows.find(
        (r) =>
            (r.minPax == null || totalPax >= r.minPax) &&
            (r.maxPax == null || totalPax <= r.maxPax)
    ) ?? period.rows[0];
    if (!row) return null;

    // Çocuk ücretleri rezervasyondaki TOPLAM YETİŞKİN sayısına denk gelen pax band'ından alınır
    // (oda başına yetişkin band'ı yerine). Yetişkin fiyatı yine yukarıdaki `row` (totalPax band'ı) ile hesaplanır.
    const totalAdults = rooms.reduce((sum, room) => sum + Math.max(0, room.adults ?? 0), 0);
    const childRow = period.rows.find(
        (r) =>
            (r.minPax == null || totalAdults >= r.minPax) &&
            (r.maxPax == null || totalAdults <= r.maxPax)
    ) ?? row;

    const perPerson = row.perPersonDouble ?? 0;
    const singleRoom = row.singleRoom ?? null;
    const extraBed = row.extraBed ?? 0;
    const childRules = childRow.childRules ?? [];
    const fallbackChildFee = childRow.childrenFee ?? 0;

    type BreakdownPart = Array<
        | { count: number; unitPrice: number }
        | { single: number }
        | { extraBed: number }
        | { child: number; amount: number }
    >;
    const roomPrices: Array<{
        roomIndex: number;
        total_price: number;
        currency?: PriceData['currency'];
        discount_amount?: number;
        applied_campaigns?: AppliedCampaign[];
        breakdownParts?: BreakdownPart;
    }> = [];
    let totalAmount = 0;

    for (let i = 0; i < rooms.length; i++) {
        const room = rooms[i];
        const adults = Math.max(0, room.adults ?? 0);
        const childAges = room.childAges ?? [];
        let roomTotal = 0;
        const breakdownParts: BreakdownPart = [];

        if (adults === 1 && singleRoom != null) {
            roomTotal += singleRoom;
            breakdownParts.push({ single: singleRoom });
        } else if (adults === 2) {
            roomTotal += perPerson * 2;
            breakdownParts.push({ count: 2, unitPrice: perPerson });
        } else if (adults >= 3) {
            const extraCount = adults - 2;
            roomTotal += perPerson * 2 + extraCount * extraBed;
            breakdownParts.push({ count: 2, unitPrice: perPerson });
            breakdownParts.push({ count: extraCount, unitPrice: extraBed });
        } else if (adults > 0) {
            roomTotal += perPerson * adults;
            breakdownParts.push({ count: adults, unitPrice: perPerson });
        }
        for (const age of childAges) {
            const rule = childRules.find((r) => age >= r.min_age && age <= r.max_age);
            const fee = rule ? rule.fee : fallbackChildFee;
            roomTotal += fee;
            breakdownParts.push({ child: 1, amount: fee });
        }
        roomPrices.push({
            roomIndex: i,
            total_price: roomTotal,
            currency,
            discount_amount: 0,
            breakdownParts,
        });
        totalAmount += roomTotal;
    }

    return {
        base_price: totalAmount,
        total_price: totalAmount,
        final_price: totalAmount,
        currency,
        roomPrices,
    };
}

/** API'den dönen kampanya bilgisi */
interface AppliedCampaign {
    id?: number;
    name?: string | Record<string, string>;
    discount_amount?: number;
    discount_percentage?: number;
}

interface PriceData {
    daily_breakdown?: {
        adults: { count: number; unit_price: number; amount: number };
        children: Array<{ age: number; amount: number }>;
    };

    base_price: number;
    tax_amount?: number;
    total_price: number;
    final_price?: number;
    /** Kampanya indirimi (tur/yat vb. – KDV dahil fiyattan düşülmüş) */
    discount_amount?: number;
    /** Uygulanan kampanyalar (admin ve frontend’de gösterim için) */
    applied_campaigns?: AppliedCampaign[];
    currency?: {
        code: string;
        symbol: string;
        symbol_position?: 'before' | 'after';
        thousands_separator?: string;
        decimal_separator?: string;
    };
    breakdown?: {
        adults?: number;
        children?: number;
        youth?: number;
        adult_price?: number;
        child_price?: number;
        youth_price?: number;
    };
    /** Oda bazlı fiyatlar (tur/ship_tour, çoklu oda) */
    roomPrices?: Array<{
        roomIndex: number;
        total_price: number;
        currency?: PriceData['currency'];
        discount_amount?: number;
        applied_campaigns?: AppliedCampaign[];
        /** Private client-side: formül detayı (2× 670 + 880 = 1.994) */
        breakdownParts?: Array<
            | { count: number; unitPrice: number }
            | { single: number }
            | { extraBed: number }
            | { child: number; amount: number }
        >;
    }>;
}

// Yaş grupları tanımları
const AGE_GROUPS = {
    CHILD: { min: 0, max: 12, label: 'Children' },
    YOUTH: { min: 13, max: 17, label: 'Youth' },
    ADULT: { min: 18, max: 999, label: 'Adult' },
};

export default function ReservationBookingSidebar({
    productId,
    productType,
    productName,
    hasPrice = false,
    bookingFlow = 'instant_online',
    bookingFlowConfigured = true,
    language = 'tr',
    languageId,
    agreements = [],
    translations = {},
    onReservationSubmit,
    className = '',
    minPax = null,
    initialRequestFormDetails,
    minRentalDuration = null,
    rentalType,
    periodStartDate = null,
    periodEndDate = null,
    packages: packagesProp = [],
    sectionLinks = [],
    showAsReservationForm = false,
    tourPricing: tourPricingProp = [],
    currency: currencyProp,
    productMetas,
    pricingPeriodType = 'private',
    agreementsLoading = false,
    requestFormTitle = null,
}: ReservationBookingSidebarProps) {
    const router = useRouter();
    const { getVariable } = useLanguageVariables();

    /** Backend'den gelen kupon hata mesajını i18n key'e çevirir; dile göre çeviri, hardcode Türkçe göstermez. */
    const getCouponErrorMessage = useCallback((backendMessage: string | null | undefined, tFn: (key: string, fallback?: string) => string): string => {
        if (!backendMessage || typeof backendMessage !== 'string') return tFn('reservation.coupon_invalid', 'Invalid coupon code.');
        const msg = backendMessage.trim().toLowerCase();
        if (msg.includes('geçerlilik tarihi') || msg.includes('validity period') || msg.includes('outside')) return tFn('reservation.coupon_error_validity');
        if (msg.includes('aktif değil') || msg.includes('not active')) return tFn('reservation.coupon_error_inactive');
        if (msg.includes('kullanım limiti dolmuş') && !msg.includes('limitiniz')) return tFn('reservation.coupon_error_usage_limit');
        if (msg.includes('limitiniz dolmuş') || msg.includes('usage limit')) return tFn('reservation.coupon_error_user_limit');
        if (msg.includes('minimum tutar') || msg.includes('minimum amount') || msg.includes('min_amount')) return tFn('reservation.coupon_error_min_amount');
        if (msg.includes('ürün tipi') || msg.includes('product type')) return tFn('reservation.coupon_error_product_type');
        if (msg.includes('bu ürün için') || msg.includes('not valid for this product')) return tFn('reservation.coupon_error_product');
        if (msg.includes('geçersiz')) return tFn('reservation.coupon_invalid', 'Invalid coupon code.');
        return tFn('reservation.coupon_invalid', 'Invalid coupon code.');
    }, []);
    const { preferences, setDepartureDate, updatePreferences } = useTravelersPreferences();
    const { isAuthenticated } = useCustomerAuth();
    const t = useCallback((key: string, fallback = '') => {
        return translations[key] || getVariable(key, fallback);
    }, [translations, getVariable]);

    const isDailyBooking = ['tour', 'ship_tour'].includes(productType) && pricingPeriodType === 'daily';
    const forceReservationForm = isDailyBooking && bookingFlowConfigured ? false : showAsReservationForm;
    const showRequestForm = shouldShowRequestForm({
        productType,
        hasPrice,
        bookingFlow,
        showAsReservationForm: forceReservationForm,
    });
    const showReservationForm = shouldShowReservationForm({
        productType,
        hasPrice,
        bookingFlow,
        showAsReservationForm: forceReservationForm,
    });
    const sidebarTitleKind = resolveSidebarFormTitleKind({
        showRequestForm,
        showReservationForm,
        pricingPeriodType,
    });
    const sidebarHeading = resolveSidebarFormHeading({
        titleKind: sidebarTitleKind,
        formTitleFromApi: showRequestForm ? requestFormTitle : null,
        t,
    });

    const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
    const [selectedCheckOutDate, setSelectedCheckOutDate] = useState<Date | undefined>(undefined);
    const [specialRequests, setSpecialRequests] = useState('');
    const [localGuestCount, setLocalGuestCount] = useState(1);
    const [localChildCount, setLocalChildCount] = useState(0);
    const [priceData, setPriceData] = useState<PriceData | null>(null);
    const [isLoadingPrice, setIsLoadingPrice] = useState(false);
    const [priceError, setPriceError] = useState<string | null>(null);
    const [agreementsState, setAgreementsState] = useState<Record<number, boolean>>({});
    const [selectedAgreement, setSelectedAgreement] = useState<Agreement | null>(null);
    const [isAgreementModalOpen, setIsAgreementModalOpen] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [isSubmittingRedirect, setIsSubmittingRedirect] = useState(false);
    const [couponCode, setCouponCode] = useState("");
    const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; name: string; discount_amount: number; discount_type?: string; discount_value?: number } | null>(null);
    const appliedCouponRef = useRef(appliedCoupon);
    appliedCouponRef.current = appliedCoupon;
    const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
    const [couponError, setCouponError] = useState<string | null>(null);
    const [dailyQuoteKey, setDailyQuoteKey] = useState('');
    const dailyCurrentKeyRef = useRef('');
    const [dailyPeriodId, setDailyPeriodId] = useState<number | undefined>();
    const [dailyRemaining, setDailyRemaining] = useState<number | null>(null);
    const [selectedPackage, setSelectedPackage] = useState<string>('');
    const [guestAccordionOpen, setGuestAccordionOpen] = useState(false);
    const captchaWidgetIdRef = useRef<number | string | null>(null);

    const packages = useMemo(() => packagesProp?.length ? packagesProp : [], [packagesProp]);
    const useClientSidePrivatePrice = Boolean(
        showAsReservationForm &&
        (productType === 'tour' || productType === 'ship_tour') &&
        (tourPricingProp?.length ?? 0) > 0 &&
        tourPricingProp.some((p) => (p.periodType ?? 'private') === 'private') &&
        pricingPeriodType !== 'daily'
    );
    const childPaxAgeThreshold = productMetas?.child_pax_age_threshold_private ?? 7;
    const minChildAgeForTour = useMemo(() => {
        const raw = productMetas?.min_ages;
        if (raw == null) return 0;
        if (typeof raw === 'number' && Number.isFinite(raw)) return Math.max(0, Math.min(11, Math.floor(raw)));
        const text = String(raw).trim();
        if (!text) return 0;
        const m = text.match(/(\d+)/);
        if (!m) return 0;
        const value = parseInt(m[1], 10);
        if (!Number.isFinite(value)) return 0;
        return Math.max(0, Math.min(11, value));
    }, [productMetas?.min_ages]);
    useEffect(() => {
        if (!isDailyBooking && packages.length > 0 && !selectedPackage) {
            setSelectedPackage(packages[0].packageName ?? '');
        }
    }, [packages, selectedPackage, isDailyBooking]);
    const { config: captchaConfig, isLoading: captchaConfigLoading } = useCaptchaConfig();
    const isCaptchaRequired = Boolean(captchaConfig?.is_enabled && captchaConfig?.site_key && captchaConfig?.provider);
    const submitReadiness = useFormSubmitReadiness({
        formLoading: false,
        agreementsLoading: showReservationForm ? agreementsLoading : false,
        captchaLoading: captchaConfigLoading,
    });

    // Kupon hata mesajı 4 saniye sonra otomatik kapatılsın
    useEffect(() => {
        if (!couponError) return;
        const t = setTimeout(() => setCouponError(null), 4000);
        return () => clearTimeout(t);
    }, [couponError]);

    const requiredAgreements = useMemo(() => 
        agreements.filter(a => a.is_required), 
        [agreements]
    );
    const optionalAgreements = useMemo(() => 
        agreements.filter(a => !a.is_required), 
        [agreements]
    );

    // Zorunlu sözleşmeler varsayılan kabul (talep/iletişim formu gibi), opsiyoneller işaretsiz
    useEffect(() => {
        const initialState: Record<number, boolean> = {};
        agreements.forEach(agreement => {
            initialState[agreement.id] = agreement.is_required === true;
        });
        setAgreementsState(initialState);
    }, [agreements]);

    // MainSearchBar / cookie'den gelen tarih ve oda bilgisini sidebar'da kullan (ziyaretçi kolaylığı)
    useEffect(() => {
        if (!preferences?.departure_date) return;
        const d = new Date(preferences.departure_date);
        if (!isNaN(d.getTime())) {
            setSelectedDate((prev) => (prev ? prev : d));
        }
    }, [preferences?.departure_date]);

    // Captcha widget (v2 / Turnstile) – talep formu ile aynı
    useEffect(() => {
        if (captchaConfigLoading || !captchaConfig?.is_enabled || !captchaConfig?.site_key || !captchaConfig?.provider) return;
        const provider = captchaConfig.provider;
        const siteKey = captchaConfig.site_key;
        const scriptUrl = captchaConfig.script_url;
        const initCaptcha = async () => {
            try {
                if (provider === "google_recaptcha_v2") {
                    await loadRecaptchaScript(scriptUrl || undefined);
                    const checkContainer = setInterval(() => {
                        const container = document.getElementById(RESERVATION_CAPTCHA_CONTAINER_ID);
                        if (container && siteKey) {
                            clearInterval(checkContainer);
                            renderRecaptchaWidget(RESERVATION_CAPTCHA_CONTAINER_ID, siteKey, () => {}).then((id) => {
                                captchaWidgetIdRef.current = id;
                            }).catch(() => {});
                        }
                    }, 100);
                    setTimeout(() => clearInterval(checkContainer), 10000);
                } else if (provider === "cloudflare_turnstile") {
                    await loadTurnstileScript(scriptUrl || undefined);
                    const checkContainer = setInterval(() => {
                        const container = document.getElementById(RESERVATION_CAPTCHA_CONTAINER_ID);
                        if (container && siteKey) {
                            clearInterval(checkContainer);
                            renderTurnstileWidget(RESERVATION_CAPTCHA_CONTAINER_ID, siteKey, () => {}).then((id) => {
                                captchaWidgetIdRef.current = id;
                            }).catch(() => {});
                        }
                    }, 100);
                    setTimeout(() => clearInterval(checkContainer), 10000);
                }
            } catch {}
        };
        const t = setTimeout(initCaptcha, 500);
        return () => {
            clearTimeout(t);
            if (captchaWidgetIdRef.current != null && provider === "google_recaptcha_v2") {
                resetRecaptchaWidget(captchaWidgetIdRef.current as number);
            } else if (captchaWidgetIdRef.current != null && provider === "cloudflare_turnstile") {
                resetTurnstileWidget(captchaWidgetIdRef.current as string);
                removeTurnstileWidget(captchaWidgetIdRef.current as string);
            }
        };
    }, [captchaConfig, captchaConfigLoading]);

    const supportsRoomSystem = ['tour', 'ship_tour'].includes(productType);
    const isTour = productType === 'tour' || productType === 'ship_tour';
    /** Günlük tur: oda kavramı yok; tek grup (yetişkin/çocuk), fiyat API'de daily */
    const useDailyTourGuestUi = isTour && pricingPeriodType === 'daily';
    const showRoomBasedTourUi = supportsRoomSystem && !useDailyTourGuestUi;

    const selectDailyDate = useCallback((date: Date) => { setSelectedDate(date); }, []);
    const dailyCalendar = useDailyTourCalendar(isDailyBooking && !showRequestForm, productId, selectedDate, selectDailyDate);
    const dailyGuests = useMemo(() => {
        const rooms = preferences?.rooms ?? [];
        const children = rooms.reduce((sum, room) => sum + (room.children ?? 0), 0);
        const ages = rooms.flatMap((room) => room.childAges ?? []);
        return {
            adults: Math.max(1, rooms.reduce((sum, room) => sum + room.adults, 0)),
            children,
            childAges: Array.from({ length: children }, (_, index) => ages[index] ?? minChildAgeForTour),
        };
    }, [preferences?.rooms, minChildAgeForTour]);

    const dailyCurrentKey = isDailyBooking ? JSON.stringify([selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '', dailyGuests]) : '';
    dailyCurrentKeyRef.current = dailyCurrentKey;

    /** Accordion başlığı: (1 room, 2 adults, 1 child) veya (3 rooms, 10 adults, 5 children); çocuk yaşları tek tek yazılmaz, uzun metin olmasın */
    const guestSummaryLabel = useMemo(() => {
        const rooms = preferences?.rooms ?? [];
        if (rooms.length === 0) return '';
        const totalAdults = rooms.reduce((s, r) => s + (r.adults ?? 0), 0);
        const totalChildren = rooms.reduce((s, r) => s + (r.children ?? 0), 0);
        const roomCount = rooms.length;
        const adultWord = totalAdults === 1 ? getVariable('reservation.adult', 'adult') : getVariable('reservation.adults', 'adults');
        const childWord = totalChildren === 1 ? getVariable('reservation.child', 'child') : getVariable('reservation.children', 'children');
        if (useDailyTourGuestUi) {
            const inner =
                totalChildren > 0
                    ? `${totalAdults} ${adultWord}, ${totalChildren} ${childWord}`
                    : `${totalAdults} ${adultWord}`;
            return `(${inner})`;
        }
        const roomWord = roomCount === 1 ? getVariable('reservation.room', 'room') : getVariable('reservation.rooms', 'rooms');
        const parts: string[] = [];
        parts.push(roomCount + ' ' + (roomCount === 1 ? roomWord.replace(/s$/, '') : roomWord));
        parts.push(totalAdults + ' ' + adultWord);
        if (totalChildren > 0) {
            parts.push(totalChildren + ' ' + childWord);
        }
        return `(${parts.join(', ')})`;
    }, [preferences?.rooms, getVariable, useDailyTourGuestUi]);

    // Yaş gruplarına göre kişi sayısını hesapla (oda bazlı; tur/ship_tour)
    const ageGroupCounts = useMemo(() => {
        if (!preferences?.rooms || preferences.rooms.length === 0) {
            return { adults: 0, youth: 0, children: 0 };
        }

        let adults = 0;
        let youth = 0;
        let children = 0;

        preferences.rooms.forEach(room => {
            adults += room.adults;
            room.childAges?.forEach(age => {
                if (age >= AGE_GROUPS.ADULT.min) {
                    adults++;
                } else if (age >= AGE_GROUPS.YOUTH.min && age <= AGE_GROUPS.YOUTH.max) {
                    youth++;
                } else {
                    children++;
                }
            });
        });

        return { adults, youth, children };
    }, [preferences?.rooms]);

    const totalTravelers = useMemo(() => {
        if (supportsRoomSystem && preferences?.rooms && preferences.rooms.length > 0) {
            return preferences.rooms.reduce((total, room) => total + room.adults + room.children, 0);
        } else {
            return localGuestCount + localChildCount;
        }
    }, [preferences?.rooms, localGuestCount, localChildCount, supportsRoomSystem]);

    const hasChildAgeBelowMinimum = useMemo(() => {
        if (!supportsRoomSystem || minChildAgeForTour <= 0) return false;
        const rooms = preferences?.rooms ?? [];
        return rooms.some((room) => (room.childAges ?? []).some((age) => age < minChildAgeForTour));
    }, [supportsRoomSystem, minChildAgeForTour, preferences?.rooms]);

    const lastPriceCalculateRef = useRef(0);

    /** Kişi/paket değişince throttle sıfırlanır; aksi halde 2 sn içinde yeni /api/price/calculate çalışmaz (ör. 2→1 yetişkin, fiyat eski kalır). */
    useEffect(() => {
        lastPriceCalculateRef.current = 0;
    }, [preferences?.rooms, localGuestCount, localChildCount, selectedPackage, pricingPeriodType, productId]);

    const calculatePrice = useCallback(async () => {
        if (!selectedDate || totalTravelers < 1) {
            return;
        }
        const now = Date.now();
        if (now - lastPriceCalculateRef.current < PRICE_CALCULATE_THROTTLE_MS) {
            return;
        }
        lastPriceCalculateRef.current = now;

        setIsLoadingPrice(true);
        setPriceError(null);

        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        let dateField = 'check_in_date';
        if (productType === 'tour' || productType === 'ship_tour' || productType === 'flight') {
            dateField = 'travel_date';
        }

        // Rezervasyondaki toplam yetişkin (tüm odalar). Çocuk fiyatı bu pax band'ından hesaplansın diye
        // her oda isteğine child_pax_count olarak gönderilir (oda başına yetişkin band'ı yerine).
        const totalAdultsForChildBand = (preferences?.rooms && preferences.rooms.length > 0)
            ? preferences.rooms.reduce((sum, r) => sum + Math.max(0, r.adults ?? 0), 0)
            : Math.max(1, localGuestCount);

        const buildRoomRequest = (paxCount: number, childCount: number, childAges: number[]) => {
            const body: Record<string, unknown> = {
                product_id: productId,
                [dateField]: dateStr,
                pax_count: Math.max(1, paxCount),
                child_count: childCount,
                child_pax_count: totalAdultsForChildBand,
                infant_count: 0,
            };
            if (childAges.length > 0) body.child_ages = JSON.stringify(childAges);
            if (['hotel', 'villa', 'yacht'].includes(productType) && selectedCheckOutDate) {
                body.check_out_date = format(selectedCheckOutDate, 'yyyy-MM-dd');
            }
            if ((productType === 'tour' || productType === 'ship_tour') && selectedPackage) {
                body.package_name = selectedPackage;
            }
            if ((productType === 'tour' || productType === 'ship_tour') && pricingPeriodType === 'daily') {
                body.pricing_period_type = 'daily';
            }
            return body;
        };

        try {
            // Tur/ship_tour (private vb.): her oda için ayrı fiyat hesapla, toplam = odalar toplamı — günlük turda tek istek
            if (showRoomBasedTourUi && preferences?.rooms && preferences.rooms.length > 0) {
                const roomResults: Array<{ roomIndex: number; total_price: number; currency?: PriceData['currency']; discount_amount?: number; applied_campaigns?: AppliedCampaign[] }> = [];
                let totalAmount = 0;
                let firstCurrency: PriceData['currency'] | undefined;

                for (let i = 0; i < preferences.rooms.length; i++) {
                    const room = preferences.rooms[i];
                    const pax = Math.max(1, room.adults);
                    const child = room.children ?? 0;
                    const ages = room.childAges ?? [];
                    const body = buildRoomRequest(pax, child, ages);

                    const response = await fetch('/api/price/calculate', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                        body: JSON.stringify(body),
                    });

                    const data = await response.json().catch(() => ({}));
                    if (!response.ok) {
                        const msg = (data as { message?: string })?.message ?? t('reservation.price_calculation_error');
                        throw new Error(msg);
                    }
                    // Backend Laravel formatResponse sadece { data } döndürür, success alanı yok; data.data varlığı yeterli
                    if (!data.data) {
                        throw new Error((data as { message?: string })?.message ?? t('reservation.price_calculation_error'));
                    }

                    const result = data.data as {
                        final_price?: number; total_price?: number; base_price?: number; currency?: PriceData['currency'];
                        discount_amount?: number; applied_campaigns?: AppliedCampaign[];
                    };
                    const roomTotal = result.final_price ?? result.total_price ?? result.base_price ?? 0;
                    const roomDiscount = result.discount_amount ?? 0;
                    roomResults.push({
                        roomIndex: i,
                        total_price: roomTotal,
                        currency: result.currency,
                        discount_amount: roomDiscount,
                        applied_campaigns: result.applied_campaigns,
                    });
                    totalAmount += roomTotal;
                    if (!firstCurrency && result.currency) firstCurrency = result.currency;
                }
                const totalDiscount = roomResults.reduce((sum, r) => sum + (r.discount_amount ?? 0), 0);
                const allCampaigns: AppliedCampaign[] = [];
                roomResults.forEach((r) => {
                    (r.applied_campaigns ?? []).forEach((c: AppliedCampaign) => {
                        if (c && !allCampaigns.some((x) => x.id === c.id)) allCampaigns.push(c);
                    });
                });
                setPriceData({
                    base_price: totalAmount + totalDiscount,
                    total_price: totalAmount,
                    final_price: totalAmount,
                    discount_amount: totalDiscount,
                    applied_campaigns: allCampaigns.length > 0 ? allCampaigns : undefined,
                    currency: firstCurrency,
                    roomPrices: roomResults,
                });
                return;
            }

            // Tek istek: tur dışı veya oda yok (local sayılar)
            let paxCount = localGuestCount;
            let childCount = localChildCount;
            let childAges: number[] = [];
            if (supportsRoomSystem && preferences?.rooms && preferences.rooms.length > 0) {
                const r = preferences.rooms[0];
                paxCount = Math.max(1, r.adults);
                childCount = r.children ?? 0;
                childAges = r.childAges ?? [];
            }

            const requestBody = buildRoomRequest(paxCount, childCount, childAges);

            const response = await fetch('/api/price/calculate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify(requestBody),
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                const msg = (data as { message?: string })?.message ?? t('reservation.price_calculation_error');
                throw new Error(msg);
            }
            // Backend Laravel formatResponse sadece { data } döndürür; data.data varlığı yeterli
            if (data.data) {
                const result = data.data as {
                    final_price?: number; total_price?: number; base_price?: number; tax_amount?: number;
                    currency?: PriceData['currency']; breakdown?: PriceData['breakdown'];
                    discount_amount?: number; applied_campaigns?: AppliedCampaign[];
                };
                const finalPrice = result.final_price ?? result.total_price ?? result.base_price ?? 0;
                const taxAmount = result.tax_amount ?? 0;
                const priceWithoutTax = finalPrice - taxAmount;
                const breakdown = result.breakdown
                    ? {
                        adults: result.breakdown.adults ?? ageGroupCounts.adults,
                        children: result.breakdown.children ?? ageGroupCounts.children,
                        youth: result.breakdown.youth ?? ageGroupCounts.youth,
                        adult_price: result.breakdown.adult_price,
                        child_price: result.breakdown.child_price,
                        youth_price: result.breakdown.youth_price,
                    }
                    : undefined;

                setPriceData({
                    base_price: priceWithoutTax,
                    tax_amount: taxAmount,
                    total_price: finalPrice,
                    final_price: result.final_price,
                    discount_amount: result.discount_amount,
                    applied_campaigns: result.applied_campaigns,
                    currency: result.currency,
                    breakdown,
                });
            } else {
                throw new Error((data as { message?: string })?.message ?? t('reservation.price_calculation_error'));
            }
        } catch (error) {
            console.error('Price calculation error:', error);
            setPriceError(error instanceof Error ? error.message : t('reservation.price_calculation_error'));
            setPriceData(null);
        } finally {
            setIsLoadingPrice(false);
        }
    }, [selectedDate, preferences?.rooms, localGuestCount, localChildCount, totalTravelers, productId, productType, supportsRoomSystem, showRoomBasedTourUi, selectedCheckOutDate, ageGroupCounts, selectedPackage, pricingPeriodType, t]);

    useEffect(() => {
        if (!selectedDate || totalTravelers <= 0) {
            setPriceData(null);
            setPriceError(null);
            if (isDailyBooking) setIsLoadingPrice(false);
            return;
        }
        if (hasChildAgeBelowMinimum) {
            if (isDailyBooking) setIsLoadingPrice(false);
            setPriceData(null);
            setPriceError(
                t(
                    'reservation.child_min_age_warning',
                    `Bu turda çocuk yaşı en az ${minChildAgeForTour} olmalıdır.`
                ).replace(/:age/g, String(minChildAgeForTour))
            );
            return;
        }
        if (isDailyBooking) {
            const abort = new AbortController();
            const couponToRevalidate = appliedCouponRef.current?.code;
            setPriceData(null);
            setPriceError(null);
            setDailyRemaining(null);
            setDailyPeriodId(undefined);
            setIsLoadingPrice(true);
            setAppliedCoupon(null);
            const timer = setTimeout(async () => {
                try {
                    const response = await fetch('/api/daily-tours/quote', {
                        method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: abort.signal,
                        body: JSON.stringify({ product_id: productId, travel_date: format(selectedDate, 'yyyy-MM-dd'),
                            pax_count: dailyGuests.adults, child_count: dailyGuests.children, child_ages: dailyGuests.childAges, language_code: language }),
                    });
                    const payload = await response.json();
                    if (!response.ok || !payload.data?.success) {
                        const messages = Object.values(payload.errors ?? {}).flat();
                        throw new Error(String(messages[0] ?? payload.message ?? t('reservation.price_calculation_error', 'Price could not be calculated.')));
                    }
                    if (abort.signal.aborted) return;
                    const result = payload.data;
                    setPriceData({ ...result, base_price: result.base_price, total_price: result.final_price });
                    setDailyPeriodId(result.period_id);
                    setDailyRemaining(result.remaining_pax);
                    setDailyQuoteKey(dailyCurrentKey);
                    if (couponToRevalidate) {
                        const couponResponse = await fetch('/api/coupons/validate', {
                            method: 'POST', credentials: 'include', signal: abort.signal,
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ code: couponToRevalidate, product_id: productId, product_type: productType, amount: result.final_price }),
                        });
                        const couponPayload = await couponResponse.json();
                        if (!abort.signal.aborted && couponResponse.ok && couponPayload.data?.coupon) {
                            const coupon = couponPayload.data.coupon;
                            setAppliedCoupon({ code: coupon.code, name: coupon.name, discount_amount: couponPayload.data.discount_amount ?? 0,
                                discount_type: coupon.discount_type, discount_value: Number(coupon.discount_value) });
                        } else if (!abort.signal.aborted) {
                            setCouponError(couponPayload.message ?? t('reservation.coupon_invalid', 'Invalid coupon code.'));
                        }
                    }
                } catch (error) {
                    if (!abort.signal.aborted) setPriceError(error instanceof Error ? error.message : 'Price could not be calculated.');
                } finally {
                    if (!abort.signal.aborted) setIsLoadingPrice(false);
                }
            }, 250);
            return () => { clearTimeout(timer); abort.abort(); };
        }
        if (useClientSidePrivatePrice && preferences?.rooms?.length) {
            const dateStr = format(selectedDate, 'yyyy-MM-dd');
            const result = calculatePrivatePriceFromTourPricing(
                tourPricingProp,
                dateStr,
                selectedPackage,
                preferences.rooms,
                childPaxAgeThreshold,
                currencyProp
            );
            setPriceData(result);
            setPriceError(null);
            return;
        }
        const timer = setTimeout(() => {
            calculatePrice();
        }, 400);
        return () => clearTimeout(timer);
    }, [isDailyBooking, dailyCurrentKey, dailyGuests, productId, productType, language, selectedDate, totalTravelers, preferences?.rooms, localGuestCount, localChildCount, ageGroupCounts, selectedPackage, useClientSidePrivatePrice, tourPricingProp, childPaxAgeThreshold, currencyProp, calculatePrice, hasChildAgeBelowMinimum, minChildAgeForTour, t]);

    const handleDateChange = useCallback((date: Date | undefined) => {
        setSelectedDate(date);
        if (date) {
            setDepartureDate(date);
        }
    }, [setDepartureDate]);

    const handleAgreementChange = (agreementId: number, checked: boolean) => {
        setAgreementsState(prev => ({
            ...prev,
            [agreementId]: checked,
        }));
    };

    const handleViewAgreement = (agreement: Agreement) => {
        setSelectedAgreement(agreement);
        setIsAgreementModalOpen(true);
    };

    const handleAgreementAccept = (agreementId: number) => {
        setAgreementsState(prev => ({
            ...prev,
            [agreementId]: true,
        }));
    };

    const effectiveMinPax = isDailyBooking ? 1 : (minPax ?? 1);

    const meetsMinPax = totalTravelers >= effectiveMinPax;
    const hasValidPrice = (priceData?.total_price ?? 0) > 0 && (!isDailyBooking || dailyQuoteKey === dailyCurrentKey);

    const isFormValid = useMemo(() => {
        if (!selectedDate || totalTravelers < 1) {
            return false;
        }
        if (!meetsMinPax) {
            return false;
        }
        if (!hasValidPrice) {
            return false;
        }
        if (hasChildAgeBelowMinimum) {
            return false;
        }
        if (agreementsLoading) {
            return false;
        }
        if (requiredAgreements.length > 0) {
            const allRequiredAccepted = requiredAgreements.every(
                (agreement) => agreementsState[agreement.id] === true,
            );
            if (!allRequiredAccepted) return false;
        }
        return submitReadiness.canSubmit;
    }, [
        selectedDate,
        totalTravelers,
        meetsMinPax,
        hasValidPrice,
        hasChildAgeBelowMinimum,
        requiredAgreements,
        agreementsState,
        agreementsLoading,
        submitReadiness.canSubmit,
    ]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isFormValid || (isDailyBooking && (isLoadingPrice || isApplyingCoupon))) {
            setSubmitError(t('reservation.form_validation_error'));
            return;
        }
        setSubmitError(null);

        let recaptchaToken = '';
        if (isCaptchaRequired && captchaConfig?.provider && captchaConfig?.site_key) {
            if (captchaConfig.provider === 'google_recaptcha_v2') {
                recaptchaToken = getRecaptchaToken(captchaWidgetIdRef.current as number | undefined) || '';
                if (!recaptchaToken) {
                    setSubmitError(t('common.captcha_required', language === 'tr' ? 'Lütfen robot doğrulamayı tamamlayın.' : 'Please complete the robot verification.'));
                    return;
                }
            } else if (captchaConfig.provider === 'google_recaptcha_v3' || captchaConfig.provider === 'google_recaptcha_enterprise') {
                try {
                    await loadRecaptchaV3Script(captchaConfig.site_key);
                    recaptchaToken = await getRecaptchaV3Token(captchaConfig.site_key, 'reservation_booking') || '';
                } catch {
                    recaptchaToken = '';
                }
                if (!recaptchaToken) {
                    setSubmitError(t('common.captcha_required', language === 'tr' ? 'Lütfen robot doğrulamayı tamamlayın.' : 'Please complete the robot verification.'));
                    return;
                }
            } else if (captchaConfig.provider === 'cloudflare_turnstile') {
                recaptchaToken = getTurnstileToken(captchaWidgetIdRef.current as string | undefined) || '';
                if (!recaptchaToken) {
                    setSubmitError(t('common.captcha_required', language === 'tr' ? 'Lütfen robot doğrulamayı tamamlayın.' : 'Please complete the robot verification.'));
                    return;
                }
            }
        }

        setIsSubmittingRedirect(true);

        const dateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '';
        let guestCount = 1;
        let childCount = 0;
        let childAges: number[] = [];
        let rooms: Array<{ adults: number; children: number; childAges?: number[] }> = [];

        if (useDailyTourGuestUi) {
            guestCount = dailyGuests.adults;
            childCount = dailyGuests.children;
            childAges = dailyGuests.childAges;
            rooms = [dailyGuests];
        } else if (supportsRoomSystem && preferences?.rooms && preferences.rooms.length > 0) {
            guestCount = preferences.rooms.reduce((sum, r) => sum + r.adults, 0);
            childCount = preferences.rooms.reduce((sum, r) => sum + (r.children ?? 0), 0);
            childAges = preferences.rooms.flatMap(r => r.childAges ?? []);
            rooms = preferences.rooms.map(r => ({
                adults: r.adults,
                children: r.children ?? 0,
                childAges: r.childAges ?? [],
            }));
        } else {
            guestCount = localGuestCount;
            childCount = localChildCount;
        }

        const couponDiscount = appliedCoupon?.discount_amount ?? 0;
        const baseTotal = priceData?.total_price ?? 0;
        const finalTotal = baseTotal > 0 ? Math.max(0, baseTotal - couponDiscount) : baseTotal;
        const couponCodeForCookie = appliedCoupon?.code ?? (couponCode?.trim() || undefined);

        const summary: Record<string, unknown> = {
            product_id: productId,
            product_type: productType,
            product_name: productName,
            travel_date: dateStr,
            check_in_date: productType === 'tour' || productType === 'ship_tour' ? undefined : dateStr,
            check_out_date: ['hotel', 'villa', 'yacht'].includes(productType) && selectedCheckOutDate
                ? format(selectedCheckOutDate, 'yyyy-MM-dd')
                : undefined,
            guest_count: guestCount,
            child_count: childCount,
            child_ages: childAges,
            rooms,
            room_prices: priceData?.roomPrices,
            total_price: finalTotal,
            currency: priceData?.currency,
            coupon_code: couponCodeForCookie,
            coupon_discount_amount: couponDiscount > 0 ? couponDiscount : undefined,
            special_requests: specialRequests || undefined,
            agreements: Object.entries(agreementsState)
                .filter(([_, accepted]) => accepted)
                .map(([id]) => ({ id: parseInt(id), accepted: true })),
            language_id: languageId,
            ...(!isDailyBooking && selectedPackage ? { package_name: selectedPackage } : {}),
            ...(isDailyBooking ? { pricing_period_type: 'daily', period_id: dailyPeriodId } : {}),
            ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
        };

        if (isAuthenticated) {
            try {
                const draftRes = await fetch('/api/customer/reservations/draft', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({
                        product_id: productId,
                        product_type: productType,
                        travel_date: dateStr,
                        check_in_date: productType === 'tour' || productType === 'ship_tour' ? undefined : dateStr,
                        check_out_date: ['hotel', 'villa', 'yacht'].includes(productType) && selectedCheckOutDate ? format(selectedCheckOutDate, 'yyyy-MM-dd') : undefined,
                        guest_count: guestCount,
                        child_count: childCount,
                        special_requests: specialRequests || undefined,
                        language_id: languageId,
                        rooms,
                        room_prices: priceData?.roomPrices,
                        total_price: finalTotal,
                        coupon_code: couponCodeForCookie,
                        ...(isDailyBooking ? { pricing_period_type: 'daily', period_id: dailyPeriodId } : {}),
                    }),
                });
                const draftData = await draftRes.json();
                if (draftRes.ok && draftData?.success && draftData?.data?.id) {
                    summary.draft_id = draftData.data.id;
                    summary.reservation_no = draftData.data.reservation_no;
                }
            } catch {
                // Draft isteği hatası - sessizce devam
            }
        }

        const maxAge = RESERVATION_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
        const value = encodeURIComponent(JSON.stringify(summary));
        document.cookie = `${RESERVATION_CHECKOUT_COOKIE}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;

        const checkoutSlug = t('reservation.checkout_path', language === 'tr' ? 'rezervasyon/onay' : 'checkout');
        router.push(`/${language}/${checkoutSlug}`);
        // Navigasyon başladı; loading state'i sayfa değişince kalkar (component unmount)
    };

    const formatPrice = (amount: number, currencyOverride?: PriceData['currency']) =>
        formatCurrencyAmount(amount, (currencyOverride ?? priceData?.currency) as CurrencyForFormat | undefined);

    const isModalLayout = showAsReservationForm;

    return (
        <motion.div
            className={isModalLayout ? `rounded-none bg-transparent shadow-none ${className}` : `bg-gray-100 rounded-lg ${className}`}
            variants={isModalLayout ? undefined : cardVariants}
            initial={isModalLayout ? undefined : 'hidden'}
            animate={isModalLayout ? undefined : 'visible'}
        >
            <div className={isModalLayout ? 'flex flex-col' : 'bg-gray-100 rounded-xl shadow-sm flex flex-col'}>
                {/* Başlık: modalda tek başlık (modal header) olduğu için form içinde göstermiyoruz */}
                {!isModalLayout && (
                    <div className="px-5 py-4 border-l-4 border-alsero-first shrink-0">
                        <h3 className="text-xl font-bold text-alsero-dark-gray">{sidebarHeading}</h3>
                    </div>
                )}

                {showRequestForm ? (
                    productType === 'yacht' ? (
                        <YachtRequestForm
                            productId={productId}
                            productType={productType}
                            minRentalDuration={minRentalDuration ?? undefined}
                            periodStartDate={periodStartDate ?? undefined}
                            periodEndDate={periodEndDate ?? undefined}
                            rentalType={rentalType}
                            language={language}
                            languageId={languageId}
                            translations={translations}
                            initialFormDetails={initialRequestFormDetails as any}
                        />
                    ) : (
                        <RequestForm
                            productId={productId}
                            productType={productType}
                            productName={productName}
                            language={language}
                            languageId={languageId}
                            translations={translations}
                            initialFormDetails={initialRequestFormDetails as any}
                        />
                    )
                ) : (
                <div className={isModalLayout ? 'reservation-modal-form' : ''}>
                <form onSubmit={handleSubmit} className={isModalLayout ? 'space-y-4' : 'p-5 space-y-4'}>
        
                {/* Tarih Seçimi */}
                <div>
                    <DatePickerCustom
                        label={productType === 'tour' || productType === 'ship_tour' 
                            ? t('reservation.travel_date', language === 'tr' ? 'Seyahat Tarihi' : 'Travel Date')
                            : productType === 'hotel' || productType === 'villa' || productType === 'yacht'
                            ? t('reservation.check_in_date', language === 'tr' ? 'Giris Tarihi' : 'Check-in Date')
                            : t('reservation.reservation_date', language === 'tr' ? 'Rezervasyon Tarihi' : 'Reservation Date')
                        }
                        selected={selectedDate}
                        onSelect={handleDateChange}
                        disabled={isDailyBooking ? dailyCalendar.disabled : { before: new Date() }}
                        month={isDailyBooking ? dailyCalendar.month : undefined}
                        onMonthChange={isDailyBooking ? dailyCalendar.onMonthChange : undefined}
                        language={isDailyBooking ? language : undefined}
                        labelClassName={isModalLayout ? 'text-base font-medium text-gray-700 mb-1.5 block' : undefined}
                        buttonClassName={isModalLayout ? 'text-base' : undefined}
                    />
                </div>

                {/* Tur/ship_tour: Paket seçimi (Gold, Standard vb.) – fiyat bu pakete göre hesaplanır */}
                {!isDailyBooking && (productType === 'tour' || productType === 'ship_tour') && packages.length > 0 && (
                    <div>
                        <label className={`text-alsero-dark-gray font-medium mb-2 block ${isModalLayout ? 'text-base' : 'text-sm'}`}>
                            {t('reservation.package', 'Paket')}
                        </label>
                        <div className="relative">
                            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                            </span>
                            <select
                                value={selectedPackage}
                                onChange={(e) => setSelectedPackage(e.target.value)}
                                className={`w-full pl-10 pr-4 py-3 rounded-lg text-alsero-dark-gray focus:ring-2 focus:ring-alsero-first transition-colors ${isModalLayout ? 'border border-gray-200 bg-white focus:border-alsero-first text-base' : 'bg-white border border-alsero-soft-white focus:border-alsero-first'}`}
                            >
                                {packages.map((pkg) => (
                                    <option key={pkg.packageName} value={pkg.packageName}>
                                        {(pkg.packageName || '').toUpperCase()}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                {/* Otel/Villa/Yacht için Check-out Date */}
                {['hotel', 'yacht'].includes(productType) && (
                    <div>
                        <DatePickerCustom
                            label={t('reservation.check_out_date')}
                            selected={selectedCheckOutDate}
                            onSelect={setSelectedCheckOutDate}
                            disabled={{ 
                                before: selectedDate || new Date() 
                            }}
                        />
                    </div>
                )}


                {/* Oda / Kişi – Modalda accordion (label + başlıkta özet); sayfada GuestRoomSelectorBlock. Günlük tur: tek grup, oda ekleme yok */}
                {showRoomBasedTourUi && (
                    <div className={isModalLayout ? 'reservation-modal-guests' : ''} {...(isModalLayout ? { 'data-modal-guests-box': true } : {})}>
                        {isModalLayout ? (
                            <div>
                                <label className="text-alsero-dark-gray text-base font-medium mb-2 block">
                                    {t('reservation.number_of_guests', 'Number of Guests')}
                                </label>
                                <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                                    <button
                                        type="button"
                                        onClick={() => setGuestAccordionOpen((v) => !v)}
                                        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left text-base font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                        aria-expanded={guestAccordionOpen}
                                    >
                                        <span>
                                            {guestSummaryLabel ? (
                                                <span className="text-alsero-dark-gray">{guestSummaryLabel}</span>
                                            ) : (
                                                <span className="text-gray-500">{t('reservation.select_rooms_guests', 'Select rooms and guests')}</span>
                                            )}
                                        </span>
                                        <svg className={`w-5 h-5 shrink-0 text-gray-500 transition-transform ${guestAccordionOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                    {guestAccordionOpen && (
                                        <div className="border-t border-gray-200 px-3 py-3">
                                            <ReservationModalGuests
                                                rooms={preferences?.rooms ?? []}
                                                onRoomsChange={(newRooms) => updatePreferences({ rooms: newRooms })}
                                                t={t}
                                                getVariable={getVariable}
                                                departureDate={selectedDate || null}
                                                minChildAge={minChildAgeForTour}
                                                maxRooms={10}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <GuestRoomSelectorBlock
                                rooms={preferences?.rooms ?? []}
                                onRoomsChange={(newRooms) => updatePreferences({ rooms: newRooms })}
                                t={t}
                                getVariable={getVariable}
                                departureDate={selectedDate || null}
                                maxRooms={10}
                                alwaysExpanded={false}
                                compactModal={false}
                            />
                        )}
                    </div>
                )}
                {useDailyTourGuestUi && (
                    <>
                        <DailyTourGuests value={dailyGuests} onChange={(guests) => updatePreferences({ rooms: [guests] })} t={t} minChildAge={minChildAgeForTour} />
                        {dailyCalendar.error && <p role="alert" className="text-sm text-red-600">{t('reservation.availability_error', language === 'tr' ? 'Müsaitlik yüklenemedi. Lütfen tekrar deneyin.' : 'Availability could not be loaded. Please try again.')}</p>}
                        {dailyRemaining !== null && <p className="text-sm text-gray-600">{t('reservation.remaining_seats', language === 'tr' ? 'Kalan yer' : 'Remaining seats')}: {dailyRemaining}</p>}
                    </>
                )}

                {/* Diğer ürün tipleri için basit kişi sayısı */}
                {!supportsRoomSystem && !isTour && (
                    <div className="space-y-3">
                        <label className="text-alsero-dark-gray text-sm font-medium block">
                            {t('reservation.guest_count')}
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label htmlFor="guest_count" className="text-xs text-alsero-dark-gray mb-1 block">
                                    {t('reservation.adults')}
                                </label>
                                <input
                                    id="guest_count"
                                    type="number"
                                    min="1"
                                    max="99"
                                    value={localGuestCount}
                                    onChange={(e) => setLocalGuestCount(Math.max(1, parseInt(e.target.value) || 1))}
                                    className="w-full px-3 py-2 bg-white border border-alsero-soft-white rounded-lg text-alsero-dark-gray text-sm focus:outline-none focus:ring-2 focus:ring-alsero-first/20"
                                />
                            </div>
                            <div>
                                <label htmlFor="child_count" className="text-xs text-alsero-dark-gray mb-1 block">
                                    {t('reservation.children')}
                                </label>
                                <input
                                    id="child_count"
                                    type="number"
                                    min="0"
                                    max="12"
                                    value={localChildCount}
                                    onChange={(e) => setLocalChildCount(Math.max(0, parseInt(e.target.value) || 0))}
                                    className="w-full px-3 py-2 bg-white border border-alsero-soft-white rounded-lg text-alsero-dark-gray text-sm focus:outline-none focus:ring-2 focus:ring-alsero-first/20"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Fiyat Hesaplama - Loading */}
                {isLoadingPrice && (
                    <div className={`flex items-center justify-center py-4 rounded-lg ${isModalLayout ? 'border border-gray-200' : 'bg-alsero-soft-white/30'}`}>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-alsero-first"></div>
                        <span className="ml-2 text-gray-600 text-sm">{t('reservation.calculating_price')}</span>
                    </div>
                )}

                {/* Fiyat Hesaplama - Hata */}
                {priceError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-red-600 text-xs">{priceError}</p>
                    </div>
                )}

                {/* Oda bazlı fiyatlar (tur/ship_tour, çoklu oda) */}
                {priceData?.roomPrices && priceData.roomPrices.length > 0 && !isLoadingPrice && preferences?.rooms && (
                    <div className={`space-y-2 pt-2 border-t ${isModalLayout ? 'border-gray-200' : 'border-alsero-soft-white'}`}>
                        {priceData.roomPrices.map((room, idx) => {
                            const roomPref = preferences.rooms[room.roomIndex];
                            const adults = roomPref?.adults ?? 0;
                            const children = roomPref?.children ?? 0;
                            const ages = roomPref?.childAges ?? [];
                            const parts: string[] = [];
                            if (adults > 0) parts.push(`${adults} ${adults === 1 ? getVariable('adult', '') : getVariable('adults', '')}`);
                            if (children > 0) {
                                const ageStr = ages.length > 0 ? ` (${ages.join(', ')} ${getVariable('years_old', 'years')})` : '';
                                parts.push(`${children} ${children === 1 ? getVariable('child', '') : getVariable('children', '')}${ageStr}`);
                            }
                            const roomLabel = getVariable('room_number', `Room ${idx + 1}`).replace('{number}', String(idx + 1));
                            const detail = parts.length > 0 ? ` – ${parts.join(', ')}` : '';
                            const currency = (room.currency ?? priceData?.currency) as CurrencyForFormat | undefined;
                            const hasBreakdown = room.breakdownParts && room.breakdownParts.length > 0;
                            return (
                                <div key={room.roomIndex} className="flex flex-col gap-0.5">
                                    <div className={`flex justify-between items-center ${isModalLayout ? 'text-base' : 'text-sm'}`}>
                                        <span className="text-alsero-dark-gray">
                                            {roomLabel}{detail}
                                        </span>
                                        <span className="font-medium text-alsero-dark-gray">{formatPrice(room.total_price, room.currency ?? priceData?.currency)}</span>
                                    </div>
                                    {hasBreakdown && currency && (
                                        <p className={`text-right text-gray-500 ${isModalLayout ? 'text-sm' : 'text-xs'}`}>
                                            {formatCurrencyBreakdown(room.breakdownParts!, room.total_price, currency)}
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Kupon Kodu - Hata absolute ile input üstünde, şık tooltip */}
                <div>
                    <label htmlFor="coupon_code" className={`text-alsero-dark-gray font-medium mb-2 block ${isModalLayout ? 'text-base' : 'text-sm'}`}>
                        {t('reservation.coupon_code')}
                    </label>
                    <div className="relative">
                        {couponError && (
                            <div
                                className="absolute left-0 right-0 z-10 mb-1.5 rounded-xl border border-red-200 bg-white px-4 py-3 text-sm text-red-800 shadow-lg"
                                style={{ bottom: '100%' }}
                                role="alert"
                            >
                                <div className="flex items-start gap-2">
                                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
                                        <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                        </svg>
                                    </span>
                                    <span className="flex-1 font-medium leading-snug">
                                        {getCouponErrorMessage(couponError, t)}
                                    </span>
                                </div>
                                <span
                                    className="absolute left-1/2 top-full -translate-x-1/2 h-0 w-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-white"
                                    style={{ filter: 'drop-shadow(0 1px 0 #fecaca)' }}
                                    aria-hidden
                                />
                            </div>
                        )}
                        <div className="flex gap-2">
                        <input
                            id="coupon_code"
                            type="text"
                            value={couponCode}
                            onChange={(e) => {
                                setCouponCode(e.target.value);
                                setCouponError(null);
                                if (appliedCoupon) setAppliedCoupon(null);
                            }}
                            className={`flex-1 px-4 py-3 bg-white rounded-lg text-alsero-dark-gray placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-alsero-first/20 focus:border-alsero-first ${isModalLayout ? 'border border-gray-200 text-base' : 'border border-alsero-soft-white text-sm'}`}
                            placeholder={t('reservation.coupon_code_placeholder')}
                            maxLength={50}
                            disabled={!!appliedCoupon}
                        />
                        {!appliedCoupon ? (
                            <button
                                type="button"
                                onClick={async () => {
                                    const code = couponCode?.trim();
                                    if (!code) return;
                                    setIsApplyingCoupon(true);
                                    setCouponError(null);
                                    try {
                                        const quoteKeyAtApply = dailyCurrentKeyRef.current;
                                        const amount = priceData?.total_price ?? 0;
                                        const res = await fetch('/api/coupons/validate', {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                                            credentials: 'include',
                                            body: JSON.stringify({
                                                code,
                                                product_id: productId,
                                                product_type: productType,
                                                amount: amount > 0 ? amount : undefined,
                                            }),
                                        });
                                        const data = await res.json();
                                        if (isDailyBooking && quoteKeyAtApply !== dailyCurrentKeyRef.current) return;
                                        if (res.ok && data?.data) {
                                            const coupon = (data.data as { coupon?: { code?: string; name?: string; discount_type?: string; discount_value?: number }; discount_amount?: number }).coupon;
                                            const discountAmount = (data.data as { discount_amount?: number }).discount_amount ?? 0;
                                            setAppliedCoupon({
                                                code: coupon?.code ?? code,
                                                name: coupon?.name ?? code,
                                                discount_amount: discountAmount,
                                                discount_type: coupon?.discount_type,
                                                discount_value: coupon?.discount_value != null ? Number(coupon.discount_value) : undefined,
                                            });
                                            setCouponError(null);
                                        } else {
                                            const raw = (data as { error?: string; message?: string })?.error ?? (data as { message?: string })?.message ?? '';
                                            setCouponError(raw || t('reservation.coupon_invalid'));
                                        }
                                    } catch {
                                        setCouponError(t('reservation.coupon_error'));
                                    } finally {
                                        setIsApplyingCoupon(false);
                                    }
                                }}
                                disabled={isApplyingCoupon || !couponCode?.trim() || !priceData?.total_price || (isDailyBooking && !hasValidPrice)}
                                className="shrink-0 px-4 py-3 bg-alsero-first text-white text-sm font-medium rounded-lg hover:bg-alsero-first/90 focus:outline-none focus:ring-2 focus:ring-alsero-first/20 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isApplyingCoupon ? t('reservation.loading') : t('reservation.coupon_apply')}
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={() => {
                                    setAppliedCoupon(null);
                                    setCouponCode('');
                                    setCouponError(null);
                                }}
                                className="shrink-0 px-4 py-3 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
                            >
                                {t('reservation.coupon_remove')}
                            </button>
                        )}
                        </div>
                    </div>
                    {appliedCoupon && appliedCoupon.discount_amount > 0 && (
                        <p className="mt-1.5 text-sm text-green-600">
                            {t('reservation.coupon_applied')}: -{formatPrice(appliedCoupon.discount_amount)}
                        </p>
                    )}
                </div>

                {/* Ödenecek toplam tutar: kampanya indirimi + kupon + toplam */}
                {priceData && !isLoadingPrice && (
                    <div className={`pt-3 space-y-2 ${isModalLayout ? 'border-t border-gray-200' : 'border-t border-alsero-soft-white'}`}>
                        {isDailyBooking && priceData.daily_breakdown && (
                            <div className="space-y-1 text-sm text-gray-600">
                                <div className="flex justify-between gap-2">
                                    <span>{t('reservation.adults', 'Adults')}: {priceData.daily_breakdown.adults.count} × {formatPrice(priceData.daily_breakdown.adults.unit_price)}</span>
                                    <span>{formatPrice(priceData.daily_breakdown.adults.amount)}</span>
                                </div>
                                {priceData.daily_breakdown.children.map((child, index) => (
                                    <div key={index} className="flex justify-between gap-2">
                                        <span>{t('reservation.child_age', 'Child age')}: {child.age}</span>
                                        <span>{formatPrice(child.amount)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        {/* Kampanya indirimi (tur/yat vb.) – indirim varsa hem tutar hem kampanya adı gösterilir */}
                        {priceData.discount_amount != null && priceData.discount_amount > 0 && (
                            <div className="flex flex-col gap-0.5">
                                <div className={`flex justify-between items-center text-gray-600 ${isModalLayout ? 'text-base' : 'text-sm'}`}>
                                    <span>{t('reservation.campaign_discount', language === 'tr' ? 'Kampanya indirimi' : 'Campaign discount')}</span>
                                    <span className="font-medium text-red-600">-{formatPrice(priceData.discount_amount)}</span>
                                </div>
                                {priceData.applied_campaigns && priceData.applied_campaigns.length > 0 && (() => {
                                    const names = priceData.applied_campaigns
                                        .map((c) => typeof c.name === 'string' ? c.name : (c.name && (language === 'tr' ? (c.name as Record<string, string>).tr : (c.name as Record<string, string>).en)) || (c.name && Object.values(c.name as Record<string, string>)[0]) || '')
                                        .filter(Boolean);
                                    return names.length > 0 ? <p className="text-xs text-alsero-dark-gray/80">{names.join(', ')}</p> : null;
                                })()}
                            </div>
                        )}
                        {appliedCoupon && appliedCoupon.discount_amount > 0 && (
                            <div className={`flex justify-between items-center text-gray-600 ${isModalLayout ? 'text-base' : 'text-sm'}`}>
                                <span>
                                    {appliedCoupon.discount_type === 'percentage' && appliedCoupon.discount_value != null
                                        ? (t('reservation.coupon_discount_label') || (language === 'tr' ? 'İndirim %{rate}' : 'Discount %{rate}')).replace('{rate}', String(appliedCoupon.discount_value))
                                        : t('reservation.coupon_applied')}
                                </span>
                                <span className="font-medium text-red-600">-{formatPrice(appliedCoupon.discount_amount)}</span>
                            </div>
                        )}
                        <div className="flex justify-between items-center">
                            <span className={`font-bold text-alsero-dark-gray ${isModalLayout ? 'text-lg' : 'text-base'}`}>{t('reservation.total_to_pay')}</span>
                            <span className={`font-bold text-alsero-first ${isModalLayout ? 'text-3xl' : 'text-2xl'}`}>
                                {formatPrice(appliedCoupon && appliedCoupon.discount_amount > 0
                                    ? Math.max(0, priceData.total_price - appliedCoupon.discount_amount)
                                    : priceData.total_price)}
                            </span>
                        </div>
                    </div>
                )}

                {/* Özel İstekler */}
                <div>
                    <label htmlFor="special_requests" className={`text-alsero-dark-gray font-medium block mb-2 ${isModalLayout ? 'text-base' : 'text-sm'}`}>
                        {t('reservation.special_requests')}
                    </label>
                    <textarea
                        id="special_requests"
                        value={specialRequests}
                        onChange={(e) => setSpecialRequests(e.target.value)}
                        rows={3}
                        maxLength={2000}
                        className={`w-full px-3 py-2 bg-white rounded-lg text-alsero-dark-gray focus:outline-none focus:ring-2 focus:ring-alsero-first/20 resize-none ${isModalLayout ? 'border border-gray-200 text-base' : 'border border-alsero-soft-white text-sm'}`}
                        placeholder={t('reservation.special_requests_placeholder')}
                    />
                </div>

                {/* Sözleşmeler – talep/iletişim formu ile aynı: zorunlular link metni, opsiyoneller checkbox + "okudum onaylıyorum" */}
                {agreements.length > 0 && (
                    <div className="space-y-3 pt-4 border-t border-[rgba(0,0,0,0.08)]">
                        <h4 className="text-alsero-dark-gray text-sm font-semibold">{t('reservation.agreements')}</h4>
                        {requiredAgreements.length > 0 && (
                            <div className="text-alsero-dark-gray text-sm">
                                {requiredAgreements.map((agreement, index) => (
                                    <span key={agreement.id}>
                                        <button
                                            type="button"
                                            onClick={() => handleViewAgreement(agreement)}
                                            className="text-alsero-first hover:underline font-semibold"
                                        >
                                            {agreement.title}
                                        </button>
                                        {index < requiredAgreements.length - 1 ? (
                                            <span className="text-alsero-dark-gray"> {t('request_form.agreement_separator', 'buradan, ')} </span>
                                        ) : (
                                            <span className="text-alsero-dark-gray"> {t('request_form.agreement_suffix', 'buradan ')} </span>
                                        )}
                                    </span>
                                ))}
                                {t('request_form.agreement_end', 'ulaşabilirsiniz.')}
                            </div>
                        )}
                        {optionalAgreements.map((agreement) => (
                            <div key={agreement.id} className="flex items-start gap-2">
                                <input
                                    type="checkbox"
                                    id={`agreement-opt-${agreement.id}`}
                                    checked={agreementsState[agreement.id] ?? false}
                                    onChange={(e) => handleAgreementChange(agreement.id, e.target.checked)}
                                    className="mt-0.5 h-4 w-4 text-alsero-first focus:ring-alsero-first border-alsero-soft-white rounded cursor-pointer"
                                />
                                <label htmlFor={`agreement-opt-${agreement.id}`} className="flex-1 text-xs text-alsero-dark-gray cursor-pointer">
                                    <button
                                        type="button"
                                        onClick={() => handleViewAgreement(agreement)}
                                        className="text-alsero-first hover:underline font-semibold"
                                    >
                                        {agreement.title}
                                    </button>
                                    {t('request_form.agreement_optional_confirm', "'ni okudum, onaylıyorum.")}
                                </label>
                            </div>
                        ))}
                    </div>
                )}

                {/* Robot doğrulama (talep formu ile aynı) */}
                {agreementsLoading && showReservationForm && (
                    <div className="flex items-center gap-2 text-sm text-alsero-dark-gray">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-alsero-first border-t-transparent" />
                        {t('request_form.agreements_loading', language === 'tr' ? 'Sözleşmeler yükleniyor...' : 'Loading agreements...')}
                    </div>
                )}
                {captchaConfigLoading && (
                    <div className="flex items-center gap-2 text-sm text-alsero-dark-gray">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-alsero-first border-t-transparent" />
                        {t('request_form.captcha_loading', language === 'tr' ? 'Doğrulama yükleniyor...' : 'Verification loading...')}
                    </div>
                )}
                {!captchaConfigLoading && captchaConfig?.is_enabled && captchaConfig?.site_key && captchaConfig?.provider && (
                    <>
                        {(captchaConfig.provider === 'google_recaptcha_v2' || captchaConfig.provider === 'cloudflare_turnstile') && (
                            <div id={RESERVATION_CAPTCHA_CONTAINER_ID} className="min-h-[78px]" />
                        )}
                        {(captchaConfig.provider === 'google_recaptcha_v3' || captchaConfig.provider === 'google_recaptcha_enterprise') && (
                            <p className="text-sm text-alsero-dark-gray/80">{t('common.captcha_auto_note', language === 'tr' ? 'Robot doğrulaması gönder sırasında otomatik yapılır.' : 'Verification is done automatically when you submit.')}</p>
                        )}
                    </>
                )}

                {/* Hata Mesajı */}
                {submitError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-red-600 text-xs">{submitError}</p>
                    </div>
                )}

                {/* Rezervasyona Devam Et → Adım 2 (checkout) yönlendirme */}
                <button
                    type="submit"
                    disabled={!isFormValid || isLoadingPrice || isSubmittingRedirect || agreementsLoading}
                    className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all ${
                        isFormValid && !isLoadingPrice && !isSubmittingRedirect
                            ? 'bg-alsero-first hover:bg-alsero-first/90 focus:outline-none focus:ring-2 focus:ring-alsero-first/20'
                            : 'bg-gray-400 cursor-not-allowed'
                    }`}
                >
                    {isSubmittingRedirect ? (
                        <span className="inline-flex items-center gap-2">
                            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            {t('reservation.redirecting')}
                        </span>
                    ) : (
                        t('reservation.proceed_booking')
                    )}
                </button>

                {!isFormValid && selectedDate && (
                    <p className="text-xs text-alsero-light-gray text-center">
                        {hasChildAgeBelowMinimum
                            ? t('reservation.child_min_age_warning', `Bu turda çocuk yaşı en az ${minChildAgeForTour} olmalıdır.`).replace(/:age/g, String(minChildAgeForTour))
                            : !meetsMinPax && effectiveMinPax > 1
                            ? t('reservation.min_pax_warning', `Minimum ${effectiveMinPax} kişilik rezervasyon yapılabilir.`).replace(/:min/g, String(effectiveMinPax))
                            : !hasValidPrice && meetsMinPax
                            ? t('reservation.price_not_available', 'Seçtiğiniz kişi sayısı için fiyat bulunamadı.')
                            : t('reservation.fill_required_fields')}
                    </p>
                )}
                </form>
                </div>
                )}
            </div>

            {selectedAgreement && (
                <AgreementModal
                    isOpen={isAgreementModalOpen}
                    onClose={() => {
                        setIsAgreementModalOpen(false);
                        setSelectedAgreement(null);
                    }}
                    agreementSlug={selectedAgreement.slug}
                    language={language}
                    agreementId={selectedAgreement.id}
                    isRequired={selectedAgreement.is_required}
                    onAccept={handleAgreementAccept}
                />
            )}
        </motion.div>
    );
}
